import {
    type Pointer,
    type Variable,
    type CompRef,
    type VariableData,
    type VariantPointer,
    type VariableType,
    type ParentsVariablesNode,
    type ChildrenVariablesNode,
    type VariableValue,
    type BuilderStyle,
    type VariableReferences,
    type VariableReferenceDescription,
    type VariablePointer,
    type PointerWithVariations,
    type VariableReferencesV2,
    type GlobalVariableReferences,
    VariableReferenceNamespace
} from '@wix/document-services-types'
import {
    DalValue,
    DmApis,
    Extension,
    ExtensionAPI,
    ExtensionPublicAPI,
    IndexKey,
    pointerUtils,
    ValidateValue,
    type DAL,
    CreateExtensionArgument
} from '@wix/document-manager-core'
import _ from 'lodash'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {HooksExtensionApi} from '../hooks/hooks'
import type {RelationshipsAPI} from '../relationships'
import {getIdFromRef} from '../../utils/dataUtils'
import * as constants from '../../constants/constants'
import * as refArrayUtils from '../../utils/refArrayUtils'
import {
    NAMESPACES_SUPPORTING_VARIABLES,
    VARIABLES_LIST_TYPE,
    SUPPORTED_VARIABLE_TYPES,
    VARIABLE_TYPES
} from './constants'
import {DATA_MODEL_HOOKS, DeserializeDataItemAfterEvent} from '../dataModel/hooks'
import {ReportableError} from '@wix/document-manager-utils'
import {deepClone} from '@wix/wix-immutable-proxy'
import {collectVariableConnections} from './variablesRelations'
import {VIEW_MODES} from '../../constants/constants'
import {isBuilderStyle} from '../../utils/builderUtils'
import type {StylesExtensionApi} from '../style/style'
import type {DataExtensionAPI} from '../data'

const {getPointer} = pointerUtils

const VARIABLES_NAMESPACE = constants.DATA_TYPES.variables
const REF_ARRAY_TYPE = constants.REF_ARRAY_DATA_TYPE
const VARIANT_RELATION_TYPE = constants.RELATION_DATA_TYPES.VARIANTS
const {DESKTOP} = constants.VIEW_MODES

const createVariablesError = (message: string) => {
    return new ReportableError({
        message,
        errorType: 'variablesError'
    })
}

const createExtension = ({experimentInstance}: CreateExtensionArgument): Extension => {
    const comparePointers = (p1: Pointer, p2: Pointer) => p1.type === p2.type && p1.id === p2.id

    const getVariablePointerFromValuePointer = (
        relationships: RelationshipsAPI['relationships'],
        dal: DAL,
        valuePointer: Pointer
    ): VariablePointer | undefined => {
        const ownerPointer = relationships.getOwningReferencesToPointer(valuePointer, VARIABLES_NAMESPACE)[0]

        if (!ownerPointer) return

        const ownerObj = dal.get(ownerPointer)

        if (ownerObj.type === VARIANT_RELATION_TYPE) {
            const splittingNodeId = refArrayUtils.variantRelation.extractFrom(ownerObj)
            return getPointer(splittingNodeId, VARIABLES_NAMESPACE) as VariablePointer
        }

        if (ownerObj.type === REF_ARRAY_TYPE) {
            return relationships.getOwningReferencesToPointer(ownerPointer, VARIABLES_NAMESPACE)[0] as VariablePointer
        }
    }

    const createExtensionAPI = ({extensionAPI, pointers, dal}: DmApis): VariablesExtensionAPI => {
        const {relationships} = extensionAPI as RelationshipsAPI
        const {dataModel} = extensionAPI as DataModelExtensionAPI
        const {variants} = extensionAPI as VariantsExtensionAPI

        // Helper functions for variable resolution
        const validateCompPointer = (componentPointer: Pointer) => {
            if (!componentPointer || !dal.has(componentPointer)) {
                throw createVariablesError('The component pointer is not passed or it does not exist')
            }
        }

        const getComponentVariablesIds = (componentPointer: Pointer): string[] => {
            const variablesItem = dataModel.components.getItem(componentPointer, VARIABLES_NAMESPACE)
            if (!variablesItem) {
                return []
            }
            return variablesItem.variables.map((variant: DalValue) => variant.id)
        }

        const validateCompAndVariablePointers = (componentPointer: Pointer, variablePointer: VariablePointer) => {
            validateCompPointer(componentPointer)
            if (!variablePointer || !dal.has(variablePointer)) {
                throw createVariablesError('The variable pointer is not passed or it does not exist')
            }
            const currentVariableIds = getComponentVariablesIds(componentPointer)
            if (!_.includes(currentVariableIds, variablePointer.id)) {
                throw createVariablesError(
                    `The component ${componentPointer.id} didn't define ${variablePointer.id} variable`
                )
            }
        }

        const validateVariants = (compPointerWithVariants: VariantPointer) => {
            _.forEach(compPointerWithVariants.variants, variant => {
                if (!dal.has(variant)) {
                    throw createVariablesError(`Passing component with non-existing variant ${variant.id}`)
                }
            })
        }

        const getVariableValueType = (variableType: string) => {
            const referenceFieldsInfo = (extensionAPI as any).schemaAPI.extractReferenceFieldsInfo(
                VARIABLES_NAMESPACE,
                variableType
            )
            const reference = _.find(
                referenceFieldsInfo,
                ref => _.isEqual(ref.path, ['value']) && !_.isEmpty(ref.refTypes)
            )
            if (!reference) {
                throw createVariablesError(`${variableType} should define the variable value type in the schema`)
            }
            return reference.refTypes[0]
        }

        const resolveVariableConnections = (
            compPointerWithVariants: CompRef,
            variablePointersToResolve: {propertyPath: string; variablePointer: VariablePointer}[],
            resolvedValue: any,
            visitedVariables: Set<string>
        ): void => {
            for (const {propertyPath, variablePointer} of variablePointersToResolve) {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    const referencedVariableData = getVariableData(
                        compPointerWithVariants,
                        variablePointer,
                        true,
                        visitedVariables
                    )
                    if (referencedVariableData?.value) {
                        _.set(resolvedValue, propertyPath, referencedVariableData.value)
                    }
                } catch (error) {
                    const message = `Failed to resolve variable ${variablePointer.id}: ${(error as Error).toString()}`
                    ;(extensionAPI as any).logger.captureError(createVariablesError(message))
                }
            }
        }

        const resolveVariables = (
            compPointerWithVariants: CompRef,
            value: any,
            visitedVariables = new Set<string>()
        ): any => {
            if (!value || typeof value !== 'object') {
                return value
            }

            if (Array.isArray(value)) {
                return value.map(item => resolveVariables(compPointerWithVariants, item, visitedVariables))
            }

            const resolvedValue = deepClone(value)
            let currentVisitedVariables = visitedVariables

            if (value.variableConnections && typeof value.variableConnections === 'object') {
                currentVisitedVariables = new Set(visitedVariables)

                // Phase 1: Collect all variable IDs and pointers from variableConnections
                const variablePointersToResolve = collectVariableConnections(dal, value.variableConnections)
                const variableIdsToResolve = variablePointersToResolve.map(({variablePointer}) => variablePointer.id)

                // Phase 2: Validate circular references and add to visited set
                for (const variableId of variableIdsToResolve) {
                    if (currentVisitedVariables.has(variableId)) {
                        throw createVariablesError('Circular variable reference detected')
                    }
                    currentVisitedVariables.add(variableId)
                }

                // Phase 3: Resolve variable connections and update the resolved value
                resolveVariableConnections(
                    compPointerWithVariants,
                    variablePointersToResolve,
                    resolvedValue,
                    currentVisitedVariables
                )

                delete resolvedValue.variableConnections
            }

            // Recursively process nested objects using the updated visited variables
            for (const [key, nestedValue] of Object.entries(resolvedValue)) {
                if (key !== 'variableConnections' && typeof nestedValue === 'object' && nestedValue !== null) {
                    resolvedValue[key] = resolveVariables(compPointerWithVariants, nestedValue, currentVisitedVariables)
                }
            }

            return resolvedValue
        }

        const getVariableData = (
            compPointerWithVariants: CompRef,
            variablePointer: VariablePointer,
            resolveConnections: boolean = true,
            visitedVariables?: Set<string>
        ) => {
            validateCompAndVariablePointers(compPointerWithVariants, variablePointer)
            validateVariants(compPointerWithVariants)

            // Create pointer with variants if needed
            const variablePointerWithVariants = compPointerWithVariants.variants
                ? {
                      ...variablePointer,
                      variants: compPointerWithVariants.variants
                  }
                : variablePointer

            // Get the data considering variants
            const variableDataItemInVariant = variants.getItemConsideringVariantsForPointer(
                variablePointerWithVariants,
                VARIABLES_NAMESPACE,
                'value'
            )

            if (!variableDataItemInVariant) {
                return null
            }

            const variableDefinition = dal.get(variablePointer)
            const variableValueType = getVariableValueType(variableDefinition.type)

            if (!resolveConnections) {
                return {
                    type: variableValueType,
                    value: variableDataItemInVariant.value,
                    variableConnections: variableDataItemInVariant.variableConnections
                }
            }
            const resolvedValue = resolveVariables(compPointerWithVariants, variableDataItemInVariant, visitedVariables)

            return {
                type: variableValueType,
                value: resolvedValue.value
            }
        }

        const getComponentsUsingVariable = (variablePointer: VariablePointer, viewMode: string = DESKTOP): Pointer[] =>
            _(relationships.getReferencesToPointer(variablePointer))
                .filter(({type}) => type !== VARIABLES_NAMESPACE)
                .flatMap(pointer => relationships.getComponentsEventuallyReferencingItem(pointer))
                .filter(({type}) => type === viewMode)
                .uniqWith(comparePointers)
                .value()

        const getAllVariablesPointers = (
            componentPointer: Pointer,
            filter: {type?: VariableType} = {}
        ): VariablePointer[] => {
            const variablesListItem = dataModel.components.getItem(componentPointer, VARIABLES_NAMESPACE)
            let variables = variablesListItem?.variables ?? []
            if (variables && filter.type) {
                variables = _.filter(variables, ({type}) => type && filter.type === type)
            }
            return _.map(variables, ({id}) => getPointer(getIdFromRef(id), VARIABLES_NAMESPACE) as VariablePointer)
        }

        const collectAncestorsVariablesIds = (componentPointer: Pointer) => {
            const ancestors = [componentPointer, ...pointers.structure.getAncestors(componentPointer)].filter(
                pointer => !_.isNil(pointer)
            )
            const variablesIds = _.reduce(
                ancestors,
                (vars: string[], ancestor) => {
                    const variablesItem = dataModel.components.getItem(ancestor, VARIABLES_NAMESPACE)
                    if (variablesItem) {
                        const compVariables = variablesItem.variables.map((variant: DalValue) => variant.id)
                        vars.push(...compVariables)
                    }
                    return vars
                },
                []
            )
            return _.uniq(variablesIds)
        }

        const connect = (obj: any, property: string, variablePointer: VariablePointer) => {
            if (_.isNil(variablePointer) || !dal.get(variablePointer)) {
                throw createVariablesError('The variable pointer is not passed or it does not exist')
            }

            if (!_.has(obj, property)) {
                throw createVariablesError('Property does not exist on object')
            }

            if (_.isArray(obj)) {
                return _.set(obj, [property, 'variableConnections', ''], variablePointer.id)
            }

            return _.set(obj, ['variableConnections', property], variablePointer.id)
        }

        const getGlobalVariablesByCssVarName = (): Record<string, string> => {
            const variablesList = dataModel.components.getItem(
                pointers.structure.getMasterPage(VIEW_MODES.DESKTOP),
                VARIABLES_NAMESPACE
            )
            if (!variablesList) {
                return {}
            }
            return _(variablesList.variables).keyBy('cssVarName').mapValues('id').value()
        }

        const getPointerByCssVarName = (compPointer: Pointer, cssVarName: string): VariablePointer | undefined => {
            const indexKey: IndexKey = dal.queryFilterGetters.getCssVarNameFilter(cssVarName)
            return dal.getIndexPointers(indexKey).find(ptr => {
                const owningCompPointers = relationships.getComponentsEventuallyReferencingItem(ptr)
                return owningCompPointers.some(comp => pointers.isSamePointer(comp, compPointer))
            }) as VariablePointer | undefined
        }

        const getByTypes = (variableTypes: Record<string, string>) => {
            const masterPagePointer = pointers.structure.getMasterPage(constants.VIEW_MODES.DESKTOP) as CompRef

            // Extract unique variable types from the values of the record
            const uniqueVariableTypes = _.uniq(Object.values(variableTypes))

            // Get all variable pointers for each type and combine them
            const allVariablePointers: VariablePointer[] = []
            for (const variableType of uniqueVariableTypes) {
                // Get variable pointers for this specific type
                const variablePointers = getAllVariablesPointers(masterPagePointer, {
                    type: variableType as VariableType
                })

                allVariablePointers.push(...variablePointers)
            }

            return allVariablePointers
        }

        // The component scope is by default the owner of the variablePointer
        // In cases where we are adding a new variable to a component, there is no owner yet,
        // so we must pass the component scope we want to validate with
        const isDuplicateCssVarName = (
            variablePointer: VariablePointer,
            cssVarName: string,
            componentScope?: Pointer
        ): boolean => {
            const indexKey: IndexKey = dal.queryFilterGetters.getCssVarNameFilter(cssVarName)
            const variablePointers = dal.getIndexPointers(indexKey) as VariablePointer[]
            if (!componentScope) {
                const owners = relationships.getComponentsEventuallyReferencingItem(variablePointer)
                componentScope = owners[0]
            }
            return (
                componentScope &&
                variablePointers
                    .filter(ptr => ptr.id !== variablePointer.id) // both are variable pointers, and we don't have to compare variants
                    .some(ptr => {
                        const ownerCompPointers: Pointer[] = relationships.getComponentsEventuallyReferencingItem(ptr)
                        return ownerCompPointers.some(comp => pointers.isSamePointer(comp, componentScope))
                    })
            )
        }

        const isCssVariableString = (value: any): value is string =>
            typeof value === 'string' && value.startsWith('var(--') && value.endsWith(')')

        const extractCssVarName = (cssVariableString: string): string => cssVariableString.slice(6, -1)

        // Currently only global vars are supported.
        // In the future, we can use the index to find all possible ones
        // but this function (updateCssVariableConnections) will need to receive the component pointer to connect to the right scope
        const updateCssVariableConnections = <T extends BuilderStyle | VariableData | VariableValue>(
            updatedItem: T
        ): T => {
            const globalVars = getGlobalVariablesByCssVarName()
            const nonExistingGlobalVariables = new Map<string, string>()

            const processItem = (obj: any, path: string[] = []): void => {
                if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
                    return
                }

                Object.entries(obj).forEach(([property, value]) => {
                    const currentPath = [...path, property]

                    if (isCssVariableString(value)) {
                        const cssVarName = extractCssVarName(value)

                        const variableId = globalVars[cssVarName]
                        if (!variableId) {
                            nonExistingGlobalVariables.set(currentPath.join('/'), cssVarName)
                        }

                        if (nonExistingGlobalVariables.size > 0) {
                            return
                        }

                        connect(
                            obj,
                            property,
                            pointerUtils.getPointer(variableId, VARIABLES_NAMESPACE) as VariablePointer
                        )
                    } else if (typeof value === 'object' && value !== null) {
                        processItem(value, currentPath)
                    }
                })
            }

            processItem(updatedItem)

            if (nonExistingGlobalVariables.size > 0) {
                const nonExistingGlobalVariablesObj = Object.fromEntries(nonExistingGlobalVariables.entries())
                throw new ReportableError({
                    message: `updateCssVariableConnections sent with non-existing global variables: ${JSON.stringify(
                        nonExistingGlobalVariablesObj
                    )}`,
                    errorType: 'updateCssVariableConnectionsInvalidVariable',
                    extras: {
                        nonExistingGlobalVariables: nonExistingGlobalVariablesObj
                    }
                })
            }

            return updatedItem
        }

        const getVariableReferencesV2 = (variablePointer: VariablePointer): VariableReferencesV2 => {
            if (!variablePointer || !dal.has(variablePointer)) {
                throw createVariablesError('The variable pointer is not passed or it does not exist')
            }

            const componentsMap = new Map<string, PointerWithVariations>()
            const globalStylesMap = new Map<string, PointerWithVariations>()
            const globalVariablesMap = new Map<string, PointerWithVariations>()

            const masterPagePointer = pointers.structure.getMasterPage(VIEW_MODES.DESKTOP)
            const globalVariableList = new Set(getAllVariablesPointers(masterPagePointer).map(p => p.id))

            const {style: styleExtension} = extensionAPI as StylesExtensionApi
            const {isGlobalStyle} = styleExtension.global
            const {data: dataExtension} = extensionAPI as DataExtensionAPI
            const {isVariantRelation} = dataExtension.variantRelation

            const isComponent = (pointer: Pointer): boolean =>
                VIEW_MODES[pointer.type] && pointer.id !== constants.MASTER_PAGE_ID

            const isGlobalVariable = (pointer: Pointer): boolean =>
                pointer.type === VARIABLES_NAMESPACE && globalVariableList.has(pointer.id)

            const isInnerElement = (pointer: Pointer): boolean => dataExtension.getItemType(pointer) === 'InnerElement'

            const getPropertyPathsFromVariableConnections = (dataItem: any): string[][] => {
                return (dataItem.variableConnections || [])
                    .filter((connection: any) => connection.variableId === variablePointer.id)
                    .map((connection: any) => connection.path.split('/'))
            }

            const addOrUpdateReference = (
                targetMap: Map<string, PointerWithVariations>,
                pointer: Pointer,
                metadata: TraversalMetadata
            ): void => {
                const {propertyPaths, innerElementPath, variantsArray, namespace} = metadata
                if (!propertyPaths?.length) return

                const entry = targetMap.get(pointer.id)
                const variations = entry?.variations || []

                const existingItem = variations.find(
                    item =>
                        item.pointer.id === pointer.id &&
                        _.isEqual(item.pointer.variants ?? null, variantsArray ?? null) &&
                        _.isEqual(item.innerElementPath ?? null, innerElementPath ?? null) &&
                        _.isEqual(item.namespace, namespace)
                )

                if (existingItem) {
                    _.forEach(propertyPaths, path => {
                        if (!_.some(existingItem.propertyPaths, existingPath => _.isEqual(existingPath, path))) {
                            existingItem.propertyPaths.push(path)
                        }
                    })
                } else {
                    variations.push(
                        _.pickBy(
                            {
                                pointer: variantsArray ? {...pointer, variants: variantsArray} : pointer,
                                namespace,
                                innerElementPath,
                                propertyPaths
                            } as VariableReferenceDescription,
                            _.negate(_.isUndefined)
                        ) as VariableReferenceDescription
                    )
                }

                targetMap.set(pointer.id, {pointer, variations})
            }

            interface TraversalMetadata {
                propertyPaths?: string[][]
                variantsArray?: Pointer[]
                innerElementPath?: string[]
                namespace?: VariableReferenceNamespace
            }

            const traverse = (ptr: Pointer, metadata: TraversalMetadata = {}) => {
                if (ptr.id === constants.MASTER_PAGE_ID) {
                    return
                }

                const data = dal.get(ptr)
                const {propertyPaths} = metadata

                if (!propertyPaths && data.variableConnections) {
                    metadata.propertyPaths = getPropertyPathsFromVariableConnections(data)
                }

                if (!metadata.namespace) {
                    if (ptr.type === constants.DATA_TYPES.theme) {
                        metadata.namespace = VariableReferenceNamespace.CSS_STYLE
                    } else if (ptr.type === constants.DATA_TYPES.layout) {
                        metadata.namespace = VariableReferenceNamespace.LAYOUT
                    }
                }

                if (isVariantRelation(data)) {
                    metadata.variantsArray = (
                        (refArrayUtils.variantRelation.extractVariants(data) || []) as string[]
                    ).map((variantId: string) => getPointer(variantId, constants.DATA_TYPES.variants))
                }

                if (isInnerElement(ptr)) {
                    const referredTypeParts = data.referredType.split('|')
                    metadata.innerElementPath =
                        referredTypeParts.length > 1 ? [referredTypeParts[1]] : referredTypeParts
                }

                if (isComponent(ptr)) {
                    addOrUpdateReference(componentsMap, ptr, metadata)
                    return
                }

                if (isGlobalStyle(ptr.id)) {
                    metadata.namespace = VariableReferenceNamespace.CSS_STYLE
                    addOrUpdateReference(globalStylesMap, ptr, metadata)
                    return
                }

                if (isGlobalVariable(ptr)) {
                    metadata.namespace = VariableReferenceNamespace.VARIABLES
                    addOrUpdateReference(globalVariablesMap, ptr, metadata)
                    return
                }

                relationships.getOwningReferencesToPointer(ptr).forEach(owner => traverse(owner, metadata))
            }

            relationships.getReferencesToPointer(variablePointer).forEach(ref => traverse(ref))

            const global: GlobalVariableReferences = {}
            const globalStyles = Array.from(globalStylesMap.values())
            const globalVariables = Array.from(globalVariablesMap.values())
            if (globalStyles.length > 0) {
                global.style = globalStyles
            }
            if (globalVariables.length > 0) {
                global.variables = globalVariables
            }

            return {
                components: Array.from(componentsMap.values()),
                global
            }
        }

        const getVariableReferences = (variablePointer: Pointer): VariableReferences => {
            if (!variablePointer || !dal.has(variablePointer)) {
                throw createVariablesError('The variable pointer is not passed or it does not exist')
            }

            const result: VariableReferences = {structure: [], style: [], variables: []}
            const visited = new Set<string>()

            const masterPagePointer = pointers.structure.getMasterPage(VIEW_MODES.DESKTOP)
            const globalVariableList = new Set(getAllVariablesPointers(masterPagePointer).map(p => p.id))

            const {style} = extensionAPI as StylesExtensionApi
            const {isGlobalStyle} = style.global

            const isComponent = (pointer: Pointer): boolean =>
                VIEW_MODES[pointer.type] && pointer.id !== constants.MASTER_PAGE_ID

            const isGlobalVariable = (pointer: Pointer): boolean =>
                pointer.type === VARIABLES_NAMESPACE && globalVariableList.has(pointer.id)

            const stopTraverseIfMatches = (
                ptr: Pointer,
                matchesStopCondition: (ptr: Pointer) => boolean,
                target: Pointer[]
            ): boolean => {
                if (matchesStopCondition(ptr) && !_.some(target, {id: ptr.id})) {
                    target.push(ptr)
                    return true
                }
                return false
            }

            const traverse = (ptr: Pointer) => {
                if (visited.has(ptr.id)) return
                visited.add(ptr.id)

                if (stopTraverseIfMatches(ptr, isComponent, result.structure)) return
                if (stopTraverseIfMatches(ptr, isGlobalVariable, result.variables)) return
                if (stopTraverseIfMatches(ptr, isGlobalStyle, result.style)) return

                const owners = relationships.getOwningReferencesToPointer(ptr)
                owners.forEach(traverse)
            }

            const directRefs = relationships.getReferencesToPointer(variablePointer)
            directRefs.forEach(traverse)

            return result
        }

        const getChildVariablesRecursively = (
            variablePointer: VariablePointer,
            visited?: Set<string>,
            getVariablePointer?: (id: string) => VariablePointer
        ) => {
            visited ??= new Set<string>()
            getVariablePointer ??= variablePointer.variants?.length
                ? (id: string) =>
                      getPointer(id, VARIABLES_NAMESPACE, {variants: variablePointer.variants}) as VariablePointer
                : (id: string) => getPointer(id, VARIABLES_NAMESPACE) as VariablePointer
            visited.add(variablePointer.id)

            const variableDataPointer = variants.getDataPointerConsideringVariants(
                variablePointer,
                VARIABLES_NAMESPACE,
                'value'
            )
            if (!variableDataPointer) {
                return
            }

            const level: ChildrenVariablesNode = {
                pointer: variablePointer,
                children: []
            }
            const next = relationships.getReferredPointers(variableDataPointer).map(p => getVariablePointer(p.id))
            if (!next.length) {
                return level
            }
            level.children = next
                .filter((pointer: VariablePointer) => !visited.has(pointer.id))
                .map((pointer: VariablePointer) => getChildVariablesRecursively(pointer, visited, getVariablePointer))
                .filter(v => v) as ChildrenVariablesNode[] //remove nullish values, if for example, there is no data for the variable in the given variant
            return level
        }

        const getParentVariablesRecursively = (
            variablePointer: VariablePointer,
            visited?: Set<string>
        ): ParentsVariablesNode => {
            visited ??= new Set<string>()
            visited.add(variablePointer.id)

            const level: ParentsVariablesNode = {
                pointer: variablePointer,
                parents: []
            }

            // Find all VariableValue nodes that reference this variable
            const referencesToVariable = relationships.getReferencesToPointer(variablePointer, VARIABLES_NAMESPACE)
            const parentVariablePointers: VariablePointer[] = []

            for (const refPointer of referencesToVariable) {
                // refPointer should be a VariableValue node that references our variable
                // We need to find which Variable owns this VariableValue
                // if we get no result, then this was probably a VariantsList or some other node in variables NS
                const parentVariablePointer = getVariablePointerFromValuePointer(relationships, dal, refPointer)

                if (
                    parentVariablePointer &&
                    !visited.has(parentVariablePointer.id) &&
                    !parentVariablePointers.some(p => p.id === parentVariablePointer.id)
                ) {
                    parentVariablePointers.push(parentVariablePointer)
                }
            }

            if (!parentVariablePointers.length) {
                return level
            }

            level.parents = parentVariablePointers
                .filter((pointer: VariablePointer) => !visited.has(pointer.id))
                .map((pointer: VariablePointer) => getParentVariablesRecursively(pointer, visited))

            return level
        }

        return {
            variables: {
                getPointerByCssVarName,
                getByTypes,
                getComponentsUsingVariable,
                list: getAllVariablesPointers,
                collectAncestorsVariablesIds,
                getVariableData,
                getComponentVariablesIds,
                getVariableValueType,
                getVariableReferences,
                getVariableReferencesV2,
                isDuplicateCssVarName,
                resolveVariables,
                updateCssVariableConnections,
                connect,
                isCssVariableString,
                extractCssVarName
            },
            global: {
                variables: {
                    getPointerByCssVarName: (cssVarName: string) => {
                        const masterPagePointer = pointers.structure.getMasterPage(VIEW_MODES.DESKTOP)
                        return getPointerByCssVarName(masterPagePointer, cssVarName)
                    },
                    getChildVariables: (variablePointer: VariablePointer) => {
                        if (!variablePointer || !dal.has(variablePointer)) {
                            throw createVariablesError('The variable pointer is not passed or it does not exist')
                        }
                        return getChildVariablesRecursively(variablePointer)
                    },
                    getParentVariables: (variablePointer: VariablePointer) => {
                        if (!variablePointer || !dal.has(variablePointer)) {
                            throw createVariablesError('The variable pointer is not passed or it does not exist')
                        }
                        return getParentVariablesRecursively(variablePointer)
                    },
                    getVariableDataByCssVarName: (cssVarName: string): any => {
                        const masterPagePointer = pointers.structure.getMasterPage(VIEW_MODES.DESKTOP) as CompRef
                        const variablePointer = getPointerByCssVarName(masterPagePointer, cssVarName)
                        if (!variablePointer) {
                            throw new Error(`No global variable found for cssVarName: ${cssVarName}`)
                        }
                        return getVariableData(masterPagePointer, variablePointer)
                    }
                }
            }
        }
    }

    const createValidator = ({dal, pointers, extensionAPI}: DmApis): Record<string, ValidateValue> => {
        const {relationships, variables} = extensionAPI as RelationshipsAPI & VariablesExtensionAPI

        const variablesListPath = ['variables']
        const variableTypes = dal.schema
            .extractReferenceFieldsInfo(VARIABLES_NAMESPACE, VARIABLES_LIST_TYPE)
            .find(({path}) => _.isEqual(path, variablesListPath))!.refTypes
        const variableTypesSet = new Set(variableTypes)

        const isEligibleForTypeValidator = (pointer: Pointer, value?: DalValue) =>
            value?.type && pointer.type === VARIABLES_NAMESPACE && value.type !== VARIANT_RELATION_TYPE

        const createValidatorResult = (type: string, message: string) => [
            {
                shouldFail: true,
                type,
                message,
                extras: {}
            }
        ]

        const createTypeValidatorResult = (type: string) =>
            createValidatorResult(type, 'Variable type is not matching the value type')

        const getIdsByRefArrayValues = (refArrayValues: string[]) => {
            return refArrayValues.map((id: string) => {
                const value = dal.get(getPointer(id, VARIABLES_NAMESPACE))
                if (value.type !== VARIANT_RELATION_TYPE) return id

                return refArrayUtils.variantRelation.extractTo(value)
            })
        }

        const getRefArrayByVariable = (variable: Variable) => {
            const references = dal.schema.getReferences(VARIABLES_NAMESPACE, variable)[0]
            if (!references) return
            const {id, refInfo} = references
            const refArrayPointer = getPointer(id, VARIABLES_NAMESPACE)
            const refArray = dal.get(refArrayPointer)
            const refArrayValues = refArrayUtils.refArray.extractValuesWithoutHash(refArray)
            const varValIds = getIdsByRefArrayValues(refArrayValues)

            return {
                refArray,
                varValIds,
                refInfo
            }
        }

        const extractRelationalSplitSchemaInfo = (pointer: Pointer, value: DalValue) =>
            dal.schema
                .extractReferenceFieldsInfo(pointer.type, value.type)
                ?.find(({isRelationalSplit}) => isRelationalSplit)

        const isEqualOrDescendant = (compPointer: Pointer, possibleAncestorPointer: Pointer) =>
            comparePointers(compPointer, possibleAncestorPointer) ||
            pointers.structure.isDescendant(compPointer, possibleAncestorPointer)

        const checkCompDescendentOfOwnersByViewMode = (
            compPointer: Pointer,
            variableOwnerCompsPointers: Pointer[],
            viewMode: string
        ) =>
            _(variableOwnerCompsPointers)
                .filter(({type}) => type === viewMode)
                .every(
                    ownerCompPointer =>
                        pointers.structure.isMasterPage(ownerCompPointer) ||
                        isEqualOrDescendant(compPointer, ownerCompPointer)
                )

        const getReferredVariablePointers = (pointer: Pointer) => {
            const referredPointers = relationships.getReferredPointers(pointer)
            return referredPointers.filter(
                referredPointer =>
                    referredPointer.type === VARIABLES_NAMESPACE && variableTypesSet.has(dal.get(referredPointer)?.type)
            ) as VariablePointer[]
        }

        const getVariableOwnerComps = (variablePointers: VariablePointer[]) => {
            return _(variablePointers)
                .flatMap(variablePointer =>
                    relationships.getOwningReferencesToPointer(variablePointer, VARIABLES_NAMESPACE)
                )
                .flatMap(variablesListPointers => relationships.getOwningReferencesToPointer(variablesListPointers))
                .uniqWith(comparePointers)
                .value()
        }

        const validateVariableType = (pointer: Pointer, value?: DalValue) => {
            if (!isEligibleForTypeValidator(pointer, value) || !variableTypesSet.has(value.type)) return

            const {varValIds, refInfo} = getRefArrayByVariable(value) ?? {}
            const varValTypes =
                refInfo &&
                varValIds?.map(varValId => dal.get(getPointer(varValId, VARIABLES_NAMESPACE, {innerPath: ['type']})))
            if (!varValTypes || varValTypes.some(varValType => !refInfo.refTypes.includes(varValType))) {
                return createTypeValidatorResult('validateVariableType')
            }
        }

        const validateVariableValueType = (pointer: Pointer, value?: DalValue) => {
            if (!isEligibleForTypeValidator(pointer, value)) return

            const relationalSplitNodePointer = getVariablePointerFromValuePointer(relationships, dal, pointer)
            const relationalSplitNode = relationalSplitNodePointer && dal.get(relationalSplitNodePointer)
            if (!relationalSplitNode) return

            const {refTypes} = extractRelationalSplitSchemaInfo(pointer, relationalSplitNode) ?? {}

            if (refTypes && !refTypes.includes(value.type)) {
                return createTypeValidatorResult('validateVariableValueType')
            }
        }

        const validateVariableHierarchicalUsage = (pointer: Pointer, value?: DalValue) => {
            // Check if the given value is eligible for the validator.
            if (!value || !NAMESPACES_SUPPORTING_VARIABLES.has(pointer.type)) return

            // Find the variables that are in use by this data item.
            const referredVariablePointers = getReferredVariablePointers(pointer)
            if (referredVariablePointers.length === 0) return

            // Find the components that own the used variables.
            const variableOwnerCompsPointers = getVariableOwnerComps(referredVariablePointers)

            // Get the component pointer of the data item (could be DESKTOP and MOBILE components).
            const variableUserCompsPointers = relationships.getComponentsEventuallyReferencingItem(pointer)

            if (_.every(variableOwnerCompsPointers, (owner: Pointer) => pointers.structure.isMasterPage(owner))) {
                return
            }

            // Make sure that the component that uses the variables is a descendent of the owners (in both DESKTOP and MOBILE),
            // or that the variable is owned by masterPage.
            for (const compPointer of variableUserCompsPointers) {
                if (!checkCompDescendentOfOwnersByViewMode(compPointer, variableOwnerCompsPointers, compPointer.type)) {
                    return createValidatorResult('validateVariableHierarchicalUsage', 'Variable is used out of scope')
                }
            }
        }

        const validateVariableUniqueCssVarName = (pointer: Pointer, value?: DalValue) => {
            if (pointer.type !== VARIABLES_NAMESPACE || !value?.cssVarName) return

            const {cssVarName} = value
            if (variables.isDuplicateCssVarName(pointer as VariablePointer, cssVarName)) {
                return createValidatorResult(
                    'validateVariableUniqueCssVarName',
                    'Variable with the same cssVarName already exists'
                )
            }
        }

        return {
            validateVariableType,
            validateVariableValueType,
            validateVariableHierarchicalUsage,
            validateVariableUniqueCssVarName
        }
    }

    const createFilters = () => ({
        getCssVarNameFilter: (namespace: string, value: DalValue): string[] => {
            if (namespace !== VARIABLES_NAMESPACE || !value?.cssVarName) {
                return []
            }

            return [value.cssVarName]
        }
    })

    const initialize = async ({extensionAPI, dal, pointers}: DmApis): Promise<void> => {
        const {hooks} = extensionAPI as HooksExtensionApi

        const findAndRemoveInnerVariableConnections = (
            prop: any,
            rootVariableConnections: any[],
            oldToNewIdMap?: Record<string, string>,
            path: any[] = []
        ) => {
            if (_.isArray(prop))
                prop.forEach((p, i) =>
                    findAndRemoveInnerVariableConnections(p, rootVariableConnections, oldToNewIdMap, [...path, i])
                )
            if (!_.isPlainObject(prop)) return

            const {variableConnections} = prop
            if (_.isPlainObject(variableConnections)) {
                delete prop.variableConnections
                _.forOwn(variableConnections, (variableId, property) => {
                    const newVariableId = oldToNewIdMap ? oldToNewIdMap[variableId] : variableId
                    if (newVariableId) {
                        rootVariableConnections.push({path: [...path, property].join('/'), variableId: newVariableId})
                    }
                })
            }

            _.forOwn(prop, (p, key) =>
                findAndRemoveInnerVariableConnections(p, rootVariableConnections, oldToNewIdMap, [...path, key])
            )
        }

        const deserializeVariablesConnections = ({
            deserializedDataItem,
            itemType,
            itemId,
            oldToNewIdMap,
            options
        }: DeserializeDataItemAfterEvent) => {
            if (options?.raw || !NAMESPACES_SUPPORTING_VARIABLES.has(itemType)) return deserializedDataItem

            if (itemType === constants.DATA_TYPES.theme && isBuilderStyle(deserializedDataItem)) {
                const {variables} = extensionAPI as VariablesExtensionAPI
                deserializedDataItem = variables.updateCssVariableConnections(deserializedDataItem)
            }

            const rootVariableConnections: any[] = []
            findAndRemoveInnerVariableConnections(deserializedDataItem, rootVariableConnections, oldToNewIdMap)

            const variableConnectionsPointer = pointers.getPointer(itemId, itemType, {
                innerPath: ['variableConnections']
            })
            if (rootVariableConnections.length > 0 || dal.has(variableConnectionsPointer)) {
                deserializedDataItem.variableConnections = rootVariableConnections
            }

            return deserializedDataItem
        }

        const serializeVariableConnectionsInDataItem = ({dataItem, options}: any) => {
            const {variableConnections} = dataItem ?? {}
            if (!_.isArray(variableConnections) || options?.raw) {
                return dataItem
            }

            delete dataItem.variableConnections

            for (const {path, variableId} of variableConnections) {
                const splittedPath = path.split('/')
                splittedPath.splice(splittedPath.length - 1, 0, constants.VARIABLE_CONNECTIONS)
                _.set(dataItem, splittedPath, variableId)
            }

            return dataItem
        }

        hooks.registerHook(DATA_MODEL_HOOKS.DESERIALIZE.DATA_ITEM_AFTER.id, deserializeVariablesConnections)
        hooks.registerHook(DATA_MODEL_HOOKS.SERIALIZE.DATA_ITEM_AFTER.id, serializeVariableConnectionsInDataItem)
    }

    const createPublicAPI = ({extensionAPI, pointers}: DmApis): VariablePublicAPI => {
        const {variables, global} = extensionAPI as VariablesExtensionAPI

        return {
            variables: {
                SUPPORTED_VARIABLE_TYPES,
                VARIABLE_TYPES,
                getVariableReferences: (variablePointer: VariablePointer) =>
                    experimentInstance.isOpen('dm_useNewGetVariableReferences')
                        ? variables.getVariableReferencesV2(variablePointer)
                        : variables.getVariableReferences(variablePointer)
            },
            global: {
                variables: {
                    getPointerByCssVarName: (cssVarName: string) => {
                        return global.variables.getPointerByCssVarName(cssVarName)
                    },
                    getByTypes: (variableTypes: Record<string, string>) => variables.getByTypes(variableTypes),
                    list: (filter: {type?: VariableType} = {}) => {
                        const masterPagePointer = pointers.structure.getMasterPage(VIEW_MODES.DESKTOP)
                        return variables.list(masterPagePointer, filter)
                    },
                    getParentVariables: (variablePointer: VariablePointer) =>
                        global.variables.getParentVariables(variablePointer),
                    getChildVariables: (variablePointer: VariablePointer) =>
                        global.variables.getChildVariables(variablePointer)
                }
            }
        }
    }

    return {
        name: 'variables',
        dependencies: new Set(['structure', 'relationships', 'dataModel', 'variants']),
        createExtensionAPI,
        createPublicAPI,
        createValidator,
        createFilters,
        initialize
    }
}

export interface VariablesExtensionAPI extends ExtensionAPI {
    variables: {
        getPointerByCssVarName(compPointer: Pointer, cssVarName: string): VariablePointer | undefined
        getByTypes(variableTypes: Record<string, string>): VariablePointer[]
        getComponentsUsingVariable(variablePointer: VariablePointer, viewMode?: string): Pointer[]
        getVariableReferences(variablePointer: VariablePointer): VariableReferences
        getVariableReferencesV2(variablePointer: VariablePointer): VariableReferencesV2
        list(componentPointer: Pointer, filter?: {type?: VariableType}): VariablePointer[]
        collectAncestorsVariablesIds(componentPointer: Pointer): string[]
        getVariableData(
            compPointerWithVariants: CompRef,
            variablePointer: VariablePointer,
            resolveConnections?: boolean
        ): any
        getComponentVariablesIds(componentPointer: Pointer): string[]
        getVariableValueType(variableType: string): string
        updateCssVariableConnections<T extends BuilderStyle | VariableData | VariableValue>(updatedItem: T): T
        connect(obj: any, property: string, variablePointer: VariablePointer): any
        resolveVariables(compPointerWithVariants: CompRef, value: any): any
        isDuplicateCssVarName(varPointer: VariablePointer, cssVarName: string, componentScope?: Pointer): boolean
        isCssVariableString(value: any): boolean
        extractCssVarName(cssVariableString: string): string
    }
    global: {
        variables: {
            getPointerByCssVarName(cssVarName: string): VariablePointer | undefined
            getChildVariables(variablePointer: VariablePointer): ChildrenVariablesNode | undefined
            getParentVariables(variablePointer: VariablePointer): ParentsVariablesNode
            getVariableDataByCssVarName(cssVarName: string): any
        }
    }
}

interface VariablePublicAPI extends ExtensionPublicAPI {
    variables: {
        getVariableReferences(variablePointer: VariablePointer): VariableReferences | VariableReferencesV2
        VARIABLE_TYPES: {CSS: Record<string, VariableType>}
        SUPPORTED_VARIABLE_TYPES: {CSS: Record<string, VariableType[]>}
    }
    global: {
        variables: {
            getPointerByCssVarName(cssVarName: string): VariablePointer | undefined
            getByTypes(variableTypes: Record<string, string>): VariablePointer[]
            list(filter: {type?: VariableType}): VariablePointer[]
            getParentVariables(variablePointer: VariablePointer): ParentsVariablesNode
            getChildVariables(variablePointer: VariablePointer): ChildrenVariablesNode | undefined
        }
    }
}

export {createExtension}
